/**
 * SoundLoader - Loads and caches audio files
 *
 * Handles fetching, decoding, and caching audio buffers.
 * Supports preloading for smooth playback.
 */

import { getAudioManager } from './AudioManager';

export class SoundLoader {
  private buffers: Map<string, AudioBuffer> = new Map();
  private loading: Map<string, Promise<AudioBuffer>> = new Map();

  /**
   * Load a single audio file
   */
  async load(url: string): Promise<AudioBuffer> {
    // Check cache first
    const cached = this.buffers.get(url);
    if (cached) {
      return cached;
    }

    // Check if already loading
    const inProgress = this.loading.get(url);
    if (inProgress) {
      return inProgress;
    }

    // Start loading
    const promise = this.fetchAndDecode(url);
    this.loading.set(url, promise);

    try {
      const buffer = await promise;
      this.buffers.set(url, buffer);
      this.loading.delete(url);
      return buffer;
    } catch (err) {
      this.loading.delete(url);
      throw err;
    }
  }

  /**
   * Load multiple files in parallel
   */
  async loadMultiple(urls: string[]): Promise<Map<string, AudioBuffer>> {
    const promises = urls.map(url =>
      this.load(url).then(buffer => ({ url, buffer }))
    );

    const results = await Promise.all(promises);
    const map = new Map<string, AudioBuffer>();

    for (const { url, buffer } of results) {
      map.set(url, buffer);
    }

    return map;
  }

  /**
   * Check if a sound is loaded
   */
  isLoaded(url: string): boolean {
    return this.buffers.has(url);
  }

  /**
   * Get a loaded buffer (returns null if not loaded)
   */
  getBuffer(url: string): AudioBuffer | null {
    return this.buffers.get(url) || null;
  }

  /**
   * Preload sounds in the background
   * Returns a promise that resolves when all are loaded
   */
  async preload(urls: string[]): Promise<void> {
    try {
      await this.loadMultiple(urls);
      console.log(`✓ Preloaded ${urls.length} sound(s)`);
    } catch (err) {
      console.error('Preload failed:', err);
    }
  }

  /**
   * Clear all cached buffers
   */
  clear(): void {
    this.buffers.clear();
    this.loading.clear();
  }

  /**
   * Get cache stats
   */
  getStats(): { loaded: number; loading: number } {
    return {
      loaded: this.buffers.size,
      loading: this.loading.size
    };
  }

  // Private helper
  private async fetchAndDecode(url: string): Promise<AudioBuffer> {
    console.log(`🔊 Loading sound: ${url}`);

    const audioManager = getAudioManager();
    const context = audioManager.getContext();

    if (!context) {
      throw new Error('AudioContext not initialized. Call unlock() first.');
    }

    // Fetch the audio file
    console.log(`  → Fetching ${url}...`);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load ${url}: ${response.statusText}`);
    }

    // Get array buffer
    console.log(`  → Decoding audio data...`);
    const arrayBuffer = await response.arrayBuffer();
    console.log(`  → Array buffer size: ${(arrayBuffer.byteLength / 1024 / 1024).toFixed(2)}MB`);

    // Decode audio data
    const audioBuffer = await context.decodeAudioData(arrayBuffer);
    console.log(`  ✓ Decoded: ${audioBuffer.duration.toFixed(2)}s, ${audioBuffer.sampleRate}Hz`);

    return audioBuffer;
  }
}

// Singleton instance
let soundLoader: SoundLoader | null = null;

export function getSoundLoader(): SoundLoader {
  if (!soundLoader) {
    soundLoader = new SoundLoader();
  }
  return soundLoader;
}
